import React, { useEffect, useRef } from "react";
import BoxPayCheckout from "./orchestrator/BoxPayCheckout";
import { clarity } from "react-microsoft-clarity";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Kutumb from "./kutumb/Kutumb";
import { toggleMobileLayout, toggleTabletLayout, setIphone, setAndroid, setNativeApp, setSafari, setChromeIos } from "./slices/MobileLayoutSlice";
import { useDispatch, useSelector } from "react-redux";
import './Utils/i18n'
import i18next from "i18next";
import ErrorBoundary from "./ErrorBoundary/ErrorBoundary";
import { checkoutCrashEvent, checkoutLoadedWithErrorAnalytics } from "./Utils/uiAnalytics";
import { isEmpty, PAYMENT_COMPLETION_PATHS } from "./Utils/utils";
import { registerDevTestHelpers } from "./Utils/devTestHelpers";

function App() {
  const dispatch = useDispatch();
  const selectedLanguage = useSelector((state) => state.commonPersisted.selectedLanguage);
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const sessionRef = useRef();

  useEffect(() => {
    if (window.self !== window.top) {
      return;
    }

    const isProd = process.env.REACT_APP_ENVIRONMENT === "PRODUCTION";
    let clarityKey;
    if (isProd) {
      const globhuntersDomain = process.env.REACT_APP_GLOBHUNTERS_DOMAIN;
      clarityKey = window.location.origin + "/" === globhuntersDomain || window.location.href.startsWith(globhuntersDomain)
        ? process.env.REACT_APP_CLARITY_GLOBHUNTERS_KEY
        : process.env.REACT_APP_CLARITY_PROD_KEY;
    } else {
      clarityKey = process.env.REACT_APP_CLARITY_TEST_KEY;
    }

    clarity.init(clarityKey);

    // setting token as a custom variable in Clarity for session replays and analytics
    const searchParams = new URLSearchParams(document.location.search);
    const token = searchParams.get("token");

    if (!isEmpty(token)) {
      clarity.setTag("token", token)
    }
    // if (isIframe) {
    //   clarity.setTag("isIframe", "Iframe");
    // }
  }, []);

  // sets flag based on screensize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      dispatch(toggleMobileLayout(width <= 680));
      dispatch(toggleTabletLayout(width >= 681 && width <= 1060));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // sets flag based on device selection
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIphone = /iphone/.test(userAgent.toLowerCase());
    const isAndroid = /android/.test(userAgent.toLowerCase());
    const isSafari = isIphone && /Safari/i.test(navigator.userAgent) && !/CriOS/i.test(navigator.userAgent);
    const isChromeIos = isIphone && /CriOS/i.test(navigator.userAgent);
    const isNativeApp = isIphone || isAndroid;
    dispatch(setIphone(isIphone));
    dispatch(setAndroid(isAndroid));
    dispatch(setSafari(isSafari));
    dispatch(setChromeIos(isChromeIos));
    dispatch(setNativeApp(isNativeApp));
  }, []);

  useEffect(() => {
    if (!selectedLanguage) return
    i18next.changeLanguage(selectedLanguage);
  }, []);


  useEffect(() => {
    sessionRef.current = sessionDetails;
  }, [sessionDetails]);

  // testing global error handling by throwing an Error
  registerDevTestHelpers(dispatch);

  useEffect(() => {
    return () => {
      window.onerror = null;
      window.onunhandledrejection = null;
    };
  }, []);


  useEffect(() => {
    // Skip on result / completion screens — not a fresh checkout load
    if (PAYMENT_COMPLETION_PATHS.some((p) => window.location.pathname.startsWith(p))) return;

    const searchParams = new URLSearchParams(document.location.search);
    const token = searchParams.get("token");
    if (!token) return;

    // Capture how long the browser took to reach this point from navigation start
    const navigationStart = window.performance?.timing?.navigationStart || null;
    const pageLoadDurationMs = navigationStart ? Date.now() - navigationStart : null;

    // Detect if this is a browser back/forward or reload vs a fresh navigation
    const navigationType = window.performance?.navigation?.type ?? null;
    const navigationTypeLabel =
      navigationType === 0 ? "navigate" :
      navigationType === 1 ? "reload" :
      navigationType === 2 ? "back_forward" : "unknown";

    checkoutLoadedWithErrorAnalytics(token, "INITIAL_LOAD", {
      info: "First App Render",
      pageLoadDurationMs,
      navigationType: navigationTypeLabel,
    });
  }, [])
  return (
    <>
      <ErrorBoundary>
        <Router>
          <Routes>
            <Route exact path="/" element={<BoxPayCheckout />} />
            <Route exact path="/kutumb/*" element={<Kutumb />} />
            <Route exact path="*" element={<BoxPayCheckout />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    </>
  );
}

export default App;